import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronDown, Calendar, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetAllLeavesAsAdminQuery,
  useDecideCfmLeaveAsAdminMutation,
} from "../../slices/leaveApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export default function AdminCfmLeaveRequests() {
  const navigate = useNavigate();

  // Get user info from Redux store (Admin info)
  const { userInfo } = useSelector((state) => state.auth);

  // Extract Admin ID from userInfo
  const adminId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const adminEmail = userInfo?.email || userInfo?.userEmail;
  const adminName = userInfo?.name || userInfo?.userName || "Admin";

  // RTK Query hooks - get all leaves as admin
  const {
    data: allLeavesResponse,
    isLoading,
    isError,
    refetch,
  } = useGetAllLeavesAsAdminQuery();

  // Use your existing academy query
  const { data: academiesData, isLoading: academiesLoading } =
    useGetAcademyQuery();

  const [decideCfmLeave] = useDecideCfmLeaveAsAdminMutation();

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");
  const [selectedApplicantType, setSelectedApplicantType] = useState("CFM");
  const [selectedLeave, setSelectedLeave] = useState(null);

  // Options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
  ];

  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
  ];

  const applicantTypeOptions = [
    { value: "CFM", label: "CFM" },
    { value: "All", label: "All" },
  ];
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [localLeaveData, setLocalLeaveData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [academyMap, setAcademyMap] = useState({});

  // Enhanced academy map building with better error handling
  useEffect(() => {
    if (academiesData) {
      let academiesArray = [];

      // Handle different possible response structures
      if (Array.isArray(academiesData)) {
        academiesArray = academiesData;
      } else if (
        academiesData.academies &&
        Array.isArray(academiesData.academies)
      ) {
        academiesArray = academiesData.academies;
      } else if (academiesData.data && Array.isArray(academiesData.data)) {
        academiesArray = academiesData.data;
      } else {
        return;
      }

      const map = {};
      academiesArray.forEach((academy) => {
        // Try multiple possible ID fields
        const academyId = academy._id || academy.id || academy.academyId;
        // Try multiple possible name fields
        const academyName =
          academy.name ||
          academy.academyName ||
          academy.academy ||
          "Unknown Academy";

        if (academyId) {
          map[academyId] = academyName;
        } else {
        }
      });

      setAcademyMap(map);
    }
  }, [academiesData]);

  // Enhanced transform function for CFM leaves with academy name
  const transformLeaveData = (data, academyMapping) => {
    if (!data || !Array.isArray(data)) return [];

    const transformedData = data.map((leave) => {
      const days = calculateLeaveDays(leave);
      const isSingleDay =
        days === 1 ||
        leave.duration === "FULL_DAY" ||
        leave.duration === "HALF_DAY";

      // Extract admin info from workflow notes if available
      const adminInfo = extractAdminInfo(leave);

      const isManualLeaveFlag = isManualLeave(leave.leaveType);
      const leaveTypeDisplay = getLeaveTypeDisplay(leave.leaveType);

      // Use original backend status for internal logic, mapped status for display
      const backendStatus = leave.status;
      const displayStatus = getStatusFromBackend(
        backendStatus,
        leave.leaveType,
      );

      // Enhanced user email and display extraction for CFM
      let userDisplay = "Unknown CFM";
      let userEmail = "";

      // Try multiple fields to get the CFM email
      userEmail =
        leave.cfmEmail || leave.Email || leave.userEmail || leave.email || "";
      userDisplay =
        userEmail ||
        leave.cfmName ||
        leave.user?.name ||
        leave.technicianName ||
        "CFM";

      // Get academy name from academyMap - ENHANCED
      const academyId = leave.academyId;
      let academyName = "Unknown Academy";

      if (academyId && academyMapping[academyId]) {
        academyName = academyMapping[academyId];
      } else if (academyId) {
        academyName = `Academy (ID: ${academyId.substring(0, 8)}...)`;
      } else {
      }

      return {
        id: leave.id || leave._id,
        user: userDisplay,
        userEmail: userEmail,
        position: "CFM",
        type: leaveTypeDisplay,
        reason: leave.reason || "No reason provided",
        days: leave.totalDays,
        startDate: leave.startDate,
        endDate: leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: displayStatus,
        backendStatus: backendStatus,

        // Academy information
        academyId: academyId,
        academyName: academyName,

        // CFM information (applicant)
        cfmNote: leave.cfmNote || "",
        cfmName: leave.cfmName || userDisplay,
        cfmEmail: leave.cfmEmail || userEmail,

        // Admin information with timestamps (for decisions)
        adminNote: leave.adminNote || adminInfo.note || "",
        adminName: leave.adminName || adminInfo.name || "",
        adminEmail: leave.managerEmail || adminInfo.email || "",
        adminNoteDate: adminInfo.date || "",

        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
        applicantRole: "CFM",
        originalLeaveType: leave.leaveType,
        isManualLeave: isManualLeaveFlag,
        isSingleDay: isSingleDay,
        originalEndDate: leave.endDate,
        _rawLeave: leave,
      };
    });

    // Sort by submitted date (newest first)
    return transformedData.sort(
      (a, b) => new Date(b.submittedDate) - new Date(a.submittedDate),
    );
  };

  // Update local data when API data changes - filter for CFM leaves
  useEffect(() => {
    if (allLeavesResponse && Object.keys(academyMap).length > 0) {
      // Handle different possible response structures
      const leavesArray = Array.isArray(allLeavesResponse)
        ? allLeavesResponse
        : allLeavesResponse?.leaves || allLeavesResponse?.data || [];

      // Filter leaves where applicant is CFM or has CFM role
      const cfmLeaves = leavesArray.filter((leave) => {
        const applicantRole = detectApplicantRole(leave);
        return applicantRole === "CFM";
      });
      const transformedData = transformLeaveData(cfmLeaves, academyMap);
      setLocalLeaveData(transformedData);
    }
  }, [allLeavesResponse, academyMap]); // Remove academiesData from dependency

  // Enhanced applicant role detection - specifically for CFM
  const detectApplicantRole = (leave) => {
    // Check explicit applicantRole first
    if (leave.applicantRole) {
      return leave.applicantRole;
    }

    // Check if it's a CFM leave - CFM applies for their own leave
    if (
      (leave.cfmId &&
        !leave.managerId &&
        !leave.supervisorId &&
        !leave.technicianId) ||
      leave.userRole === "CFM" ||
      (leave.notes &&
        leave.notes.some(
          (note) => note.actorRole === "CFM" && note.action === "APPLIED",
        ))
    ) {
      return "CFM";
    }

    return "OTHER";
  };

  const extractAdminInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    const adminNotes = leave.notes.filter(
      (note) => note.actorRole === "ADMIN" || note.actorRole === "Admin",
    );

    if (adminNotes.length > 0) {
      const latestNote = adminNotes[adminNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Admin",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || "",
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  const calculateLeaveDays = (leave) => {
    if (!leave.startDate) return 0;

    switch (leave.duration) {
      case "HALF_DAY":
        return 0.5;
      case "FULL_DAY":
        return 1;
      case "MULTI_DAY":
        if (leave.endDate) {
          const start = new Date(leave.startDate);
          const end = new Date(leave.endDate);
          const diffTime = Math.abs(end - start);
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        return 1;
      default:
        return 1;
    }
  };

  const getLeaveTypeDisplay = (backendLeaveType) => {
    if (backendLeaveType === "CASUAL") {
      return "Casual Leave";
    } else if (backendLeaveType === "EARN") {
      return "Earned Leave";
    } else {
      return "Manual Leave";
    }
  };

  // Map backend status to frontend status for CFM leaves
  const getStatusFromBackend = (backendStatus, leaveType) => {
    if (isManualLeave(leaveType)) {
      return "Approved";
    }

    const statusMap = {
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      ESCALATED: "Approved",
    };

    const normalizedStatus = (backendStatus || "PENDING").toUpperCase();
    return statusMap[normalizedStatus] || "Pending";
  };

  const isManualLeave = (leaveType) => {
    return leaveType !== "CASUAL" && leaveType !== "EARN";
  };

  // Check what actions are required for Admin
  const requiresAdminAction = (leave) => {
    if (!leave) return false;

    if (leave.isManualLeave) return false;

    // If Admin has already taken action, no action needed
    if (
      leave.adminNote ||
      leave.status === "Approved" ||
      leave.status === "Rejected"
    ) {
      return false;
    }

    // CFM leaves that are PENDING require Admin action
    if (leave.applicantRole === "CFM" && leave.backendStatus === "PENDING") {
      return true;
    }

    return false;
  };

  const getStatusCounts = () => {
    const counts = {
      pending: localLeaveData.filter((leave) => leave.status === "Pending")
        .length,
      approved: localLeaveData.filter((leave) => leave.status === "Approved")
        .length,
      rejected: localLeaveData.filter((leave) => leave.status === "Rejected")
        .length,
      total: localLeaveData.length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  const handleBack = () => {
    navigate("/admin/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getApplicantTypeColor = (applicantRole) => {
    switch (applicantRole) {
      case "CFM":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateForDisplay = (leave, field) => {
    if (field === "endDate") {
      if (leave.isSingleDay || leave.startDate === leave.endDate) {
        return "-";
      }
    }
    return formatDate(leave[field]);
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
    setActionNote("");
    setNoteError("");
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
    setActionNote("");
    setNoteError("");
  };

  const validateNote = () => {
    if (!actionNote.trim()) {
      setNoteError("Please provide a note for your decision");
      return false;
    }
    setNoteError("");
    return true;
  };

  // CORRECTED: Handle Admin decision for CFM leaves
  const handleAdminDecision = async (decision) => {
    if (!validateNote() || !selectedLeave) return;

    try {
      // Immediately update the local state to reflect the change
      setLocalLeaveData((prevData) =>
        prevData.map((leave) =>
          leave.id === selectedLeave.id
            ? {
                ...leave,
                status: decision === "APPROVE" ? "Approved" : "Rejected",
                backendStatus: decision === "APPROVE" ? "APPROVED" : "REJECTED",
                adminNote: actionNote,
                adminName: adminName,
                adminEmail: adminEmail,
                adminNoteDate: new Date().toISOString(),
              }
            : leave,
        ),
      );

      // Show success message immediately
      toast.success(`CFM Leave ${decision.toLowerCase()} successfully!`);

      // Close the details modal immediately
      handleCloseDetails();

      // Make the API call in background
      decideCfmLeave({
        leaveId: selectedLeave.id,
        decision: decision,
        note: actionNote,
      })
        .then((result) => {
          // Refetch the data to sync with server
          refetch();
        })
        .catch((error) => {
          console.error(`API failed but UI is updated:`, error);
          // Still refetch to ensure data is current
          refetch();
        });
    } catch (error) {
      console.error(`Unexpected error:`, error);
      // Even if there's an unexpected error, we've already shown success
      handleCloseDetails();
      refetch();
    }
  };

  const filteredData = localLeaveData.filter((record) => {
    const statusMatch =
      selectedStatus === "All" ||
      (selectedStatus === "Action Required"
        ? requiresAdminAction(record)
        : record.status === selectedStatus);
    const leaveTypeMatch =
      selectedLeaveType === "All" || record.type === selectedLeaveType;
    const applicantTypeMatch =
      selectedApplicantType === "All" ||
      record.applicantRole === selectedApplicantType;

    return statusMatch && leaveTypeMatch && applicantTypeMatch;
  });

  // Show loading if Admin info is not available or data is loading
  if (!adminId || isLoading || academiesLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">
            Loading CFM leave requests and academy data...
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error loading CFM leave requests. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="rounded-full"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">CFM Leave Requests</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Review and manage CFM leave applications
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b bg-card p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 sm:flex-none sm:w-64">
          <label className="block text-sm font-medium mb-2">Status</label>
          <CustomSelect
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="All"
            minWidth={160}
          />
        </div>
        <div className="flex-1 sm:flex-none sm:w-64">
          <label className="block text-sm font-medium mb-2">Leave Type</label>
          <CustomSelect
            options={leaveTypeOptions}
            value={selectedLeaveType}
            onChange={setSelectedLeaveType}
            placeholder="All"
            minWidth={160}
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>User</TableHead>
                <TableHead className="w-32">Type</TableHead>
                <TableHead className="w-40">Academy</TableHead>
                <TableHead className="w-20">Days</TableHead>
                <TableHead className="w-36">Submitted</TableHead>
                <TableHead className="w-32">From</TableHead>
                <TableHead className="w-32">To</TableHead>
                <TableHead className="w-28">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground h-32"
                  >
                    No leave requests found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((record) => (
                  <TableRow
                    key={record.id}
                    className="cursor-pointer"
                    onClick={() => handleRowClick(record)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{record.user}</div>
                        <div className="text-sm text-muted-foreground">
                          {record.position}
                        </div>
                        {record.isManualLeave && (
                          <Badge variant="outline" className="text-xs mt-1">
                            Manual Leave
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{record.type}</TableCell>
                    <TableCell>{record.academyName}</TableCell>
                    <TableCell>
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(record.submittedDate)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(record.startDate)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {record.isSingleDay
                        ? formatDate(record.startDate)
                        : formatDate(record.endDate)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          record.status === "Approved"
                            ? "success"
                            : record.status === "Rejected"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {record.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Enhanced Leave Details Dialog */}
      {showLeaveDetails && selectedLeave && (
        <Dialog
          open={showLeaveDetails}
          onOpenChange={() => setShowLeaveDetails(false)}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Leave Request Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Status and Applicant Badges */}
              <div className="flex gap-2 flex-wrap">
                <Badge
                  variant={
                    selectedLeave.status === "Approved"
                      ? "success"
                      : selectedLeave.status === "Rejected"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {selectedLeave.status}
                </Badge>
                <Badge variant="outline">CFM</Badge>
              </div>

              {/* CFM Profile */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-300 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {selectedLeave.user.charAt(0)}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold">{selectedLeave.user}</h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedLeave.position}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedLeave.email}
                  </p>
                </div>
              </div>

              {/* Leave Details */}
              <div className="space-y-3 pt-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <Label>Leave Type</Label>
                  <span className="font-medium">{selectedLeave.type}</span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <Label>Academy</Label>
                  <span className="font-medium">
                    {selectedLeave.academyName}
                  </span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <Label>Submitted On</Label>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(selectedLeave.submittedDate)}
                  </span>
                </div>

                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b pb-2">
                    <Label>Date</Label>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(selectedLeave.startDate)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b pb-2">
                      <Label>From</Label>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(selectedLeave.startDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <Label>To</Label>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b pb-2">
                  <Label>Days</Label>
                  <span className="font-medium">
                    {selectedLeave.days}{" "}
                    {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-muted rounded-lg p-4 space-y-4">
                {selectedLeave.isManualLeave ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">
                      Manual Leave Approved
                    </h4>
                    <p className="text-green-700 text-sm">
                      Manual leaves are automatically approved and do not
                      require review.
                    </p>
                  </div>
                ) : (
                  <>
                    {selectedLeave.cfmNote && (
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 font-semibold text-sm">
                              {selectedLeave.cfmName?.charAt(0) || "C"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm">
                              {selectedLeave.cfmEmail}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              CFM (Applicant)
                            </p>
                          </div>
                        </div>
                        <p className="text-sm">{selectedLeave.cfmNote}</p>
                      </div>
                    )}

                    {selectedLeave.adminNote && (
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-sm">
                              {selectedLeave.adminName?.charAt(0) || "A"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm">
                              {selectedLeave.adminEmail || "Admin"}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              Admin
                            </p>
                          </div>
                        </div>
                        <p className="text-sm">{selectedLeave.adminNote}</p>
                        {selectedLeave.adminNoteDate && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Decision on:{" "}
                            {formatDateTime(selectedLeave.adminNoteDate)}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Section for Admin */}
              {requiresAdminAction(selectedLeave) && (
                <div className="space-y-4 pt-4">
                  <div>
                    <Label className="mb-2">Note for your decision</Label>
                    <Textarea
                      value={actionNote}
                      onChange={(e) => {
                        setActionNote(e.target.value);
                        if (noteError && e.target.value.trim()) {
                          setNoteError("");
                        }
                      }}
                      rows={3}
                      placeholder="Enter your note here..."
                      className={noteError ? "border-red-500" : ""}
                    />
                    {noteError && (
                      <p className="text-red-500 text-sm mt-1">⚠ {noteError}</p>
                    )}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button
                      onClick={() => handleAdminDecision("REJECT")}
                      variant="destructive"
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Processing..." : "Reject"}
                    </Button>
                    <Button
                      onClick={() => handleAdminDecision("APPROVE")}
                      variant="default"
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Processing..." : "Approve"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
